import React, { useLayoutEffect, useState } from "react";

import { endsWith } from "lodash/fp";

import { Collapse } from "@mui/material";

import useMeasure from "@app/common/hooks/useMeasure";

import SeeMoreButton, { SeeMoreButtonProps } from "@rui/SeeMoreButton";
import Stack from "@rui/Stack";

interface ExpandableTextProps {
  buttonProps?: Omit<SeeMoreButtonProps, "hide" | "viewingMore">;
  children: string | null;
  defaultExpanded?: boolean;
  maxLength: number;
  // graceLength is the minimum number of characters that will be hidden
  // when in a collapsed state. This avoids awkward UX when the text is just over
  // the maxLength
  graceLength?: number;
}

const ExpandableText = ({
  buttonProps,
  children,
  defaultExpanded = false,
  maxLength,
  graceLength = 20,
}: ExpandableTextProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isToggling, setIsToggling] = useState(false);
  const [textRef, { height: textHeight }] = useMeasure();
  const [height, setHeight] = useState(textHeight);

  const handleTransitionEnd = () => {
    setTimeout(() => {
      setIsToggling(false);
    }, 100);
  };

  useLayoutEffect(() => {
    if (!isToggling) return;

    if (!expanded) {
      setHeight(!expanded ? textHeight : 0);
      setTimeout(() => {
        setIsToggling(false);
        setExpanded(true);
      }, 0);
    } else {
      setExpanded(false);
    }
  }, [isToggling]);

  const startTransition = () => setIsToggling(true);

  const expandable = children && children.length > maxLength + graceLength;

  if (!children) return null;

  if (!expandable) {
    return <Stack>{children}</Stack>;
  }

  const textSnippet = children.slice(0, maxLength);

  return (
    <Stack>
      <span ref={textRef}>
        {!isToggling &&
          !expanded &&
          `${textSnippet}${endsWith(".", textSnippet) ? ".." : "..."} `}
        {!expanded && !isToggling && (
          <SeeMoreButton
            {...buttonProps}
            hide={!expandable}
            viewingMore={expanded}
            onClick={startTransition}
          />
        )}
      </span>

      {(isToggling || expanded) && (
        <Collapse
          in={expanded}
          collapsedSize={height}
          timeout={200}
          addEndListener={handleTransitionEnd}
        >
          {children}{" "}
          <SeeMoreButton
            {...buttonProps}
            hide={!expandable}
            viewingMore={expanded}
            onClick={startTransition}
          />
        </Collapse>
      )}
    </Stack>
  );
};

export default ExpandableText;
